import React, { createContext, useReducer, useContext, ReactNode } from 'react'

import { ContactFormFields, InsuranceHolder } from '@customTypes/ContactFormFields'
import { BookingFor } from '@customTypes/BookingFor'

type SetFields = {
  type: 'setFields'
  payload: ContactFormFields
}

type SetField = {
  type: 'setField'
  payload: {
    fieldName: keyof ContactFormFields
    value: ContactFormFields[keyof ContactFormFields]
  }
}

type Action = SetFields | SetField
type State = ContactFormFields
type Dispatch = (action: Action) => void
type ContactDetailsProviderProps = { children: ReactNode }

const ContactDetailsContext = createContext<State | undefined>(undefined)
const ContactDetailsDispatchContext = createContext<Dispatch | undefined>(undefined)

const defaultState = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  consent: false,
  dentalTosAccepted: true,
  gender: 'female',
  bookingFor: BookingFor.BOOKING_FOR_ME,
  patient_child: false,
  patient_gender: 'patient_female',
  patient_firstName: '',
  patient_lastName: '',
  insurance_holder: InsuranceHolder.Contact,
  birthdate: '',
  patient_birthdate: '',
}

const contactDetialsReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'setFields':
      return { ...state, ...action.payload }
    case 'setField':
      return { ...state, [action.payload.fieldName]: action.payload.value }
    default: {
      return state
    }
  }
}

export const ContactDetailsProvider: React.FC<ContactDetailsProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(contactDetialsReducer, defaultState)
  return (
    <ContactDetailsContext.Provider value={state}>
      <ContactDetailsDispatchContext.Provider value={dispatch}>{children}</ContactDetailsDispatchContext.Provider>
    </ContactDetailsContext.Provider>
  )
}

export const useContactDetailsState = () => {
  const context = useContext(ContactDetailsContext)
  if (context === undefined) {
    throw new Error('useContactDetailsState must be user wuthin a ContactDetailsProvider')
  }
  return context
}

export const useContactDetailsDispatch = () => {
  const context = useContext(ContactDetailsDispatchContext)
  if (context === undefined) {
    throw new Error('useContactDetailsDispatch must be used within a ContactDetailsProvider')
  }
  return context
}

export const setContactDetailsFields = (payload: ContactFormFields): SetFields => ({
  type: 'setFields',
  payload,
})

export const setContactDetailsField = <T extends keyof ContactFormFields>(
  fieldName: T,
  value: ContactFormFields[T]
): SetField => ({ type: 'setField', payload: { fieldName, value } })
